import * as React from 'react';
import { Box, Grid, Skeleton } from '@mui/material';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
	CerebroStyledBox,
	CerebroForm,
	DebtCapitalHeading,
} from '.';
import { ReactComponent as Refinance } from '../icons/Refinance.svg';
import { DealRequestAllocation } from '../models';
import { Field, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { getApiResponseData } from '../utils/axios';
import { BooleanUtils, HtmlUtils, NumberUtils } from '../../shared/utils';
import { DebtCapitalSectionProps } from '../../pages';
import { CerebroApiEndpoints } from '../utils/api';
import { QueryKeys, useDebtScheduleQuery } from '../hooks';

export const CerebroDebtRefinance: React.FC<DebtCapitalSectionProps> = ({
	allocation,
	drawerOpenByDefault = false,
}) => {
	const [open, setOpen] = React.useState(drawerOpenByDefault);
	const api = useApi();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();

	const isEmpty = isRefinanceEmpty(allocation);

	const { mutate } = useMutation(
		async (values: DealRequestAllocationRefinanceValues) => {
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);

				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);

	const debtScheduleQuery = useDebtScheduleQuery(guid);

	if (debtScheduleQuery.isLoading) {
		return <Skeleton height={400} />;
	}

	const amountRequested = NumberUtils.Sum(
		...(debtScheduleQuery.data?.filter(item => item.isRefinance == true).map(item => item.presentBalance ?? 0) ??
			[])
	);

	return (
		<>
			<DebtCapitalHeading icon={Refinance} amountRequested={amountRequested}>
				Refinance
			</DebtCapitalHeading>

			{isEmpty ? (
				<>
					<CerebroText variant="hint" gutterBottom>
						Details will be added in the Existing Debt Page
					</CerebroText>

					<CerebroStyledBox sx={{ marginBottom: 8 }}>
						<CerebroHeading size="h2" component="h2">
							No details have been added yet.
						</CerebroHeading>
						<br />
						<br />
						<CerebroPrimaryButton wide onClick={() => setOpen(true)}>
							Add Refinance Details
						</CerebroPrimaryButton>
						<br />
					</CerebroStyledBox>
				</>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }} onEdit={() => setOpen(true)}>
					<Grid container>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								The loan(s) will be maturing soon
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceMaturingSoon)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Looking for lower rate on my debt
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceLowerRate)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Extend amortization to improve free cash flow
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceExtendAmortization)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Refinance and increase the size of the loan
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceIncreaseLoanSize)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								I am looking to exit the relationship with the current lender
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceNewLender)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>Other</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceOther)}
							</CerebroText>
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={Refinance}>
				<Box sx={{ maxWidth: 600 }}>
					<CerebroHeading gutterBottom>Refinance</CerebroHeading>
					<CerebroText gutterBottom>
						Select the reasons why you are looking to refinance. You will have the opportunity to enter the
						details for the debt you would like to refinance on the Existing Debt page.
					</CerebroText>
					<br />
					<Formik
						initialValues={{
							refinanceMaturingSoon: allocation.refinanceMaturingSoon,
							refinanceLowerRate: allocation.refinanceLowerRate,
							refinanceExtendAmortization: allocation.refinanceExtendAmortization,
							refinanceIncreaseLoanSize: allocation.refinanceIncreaseLoanSize,
							refinanceNewLender: allocation.refinanceNewLender,
							refinanceOther: allocation.refinanceOther,
						}}
						validate={validateForm}
						onSubmit={values => mutate(values)}
					>
						{({ isSubmitting, errors, touched, handleSubmit }) => {
							const error = errors.refinanceMaturingSoon !== undefined && touched.refinanceMaturingSoon;

							return (
								<CerebroForm
									flatIndicator
									onSubmit={handleSubmit}
									scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
								>
									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceMaturingSoon">
											The loan(s) will be maturing soon
										</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceMaturingSoon"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceLowerRate">
											Looking for lower rate on my debt
										</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceLowerRate"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceExtendAmortization">
											Extend amortization to improve free cash flow
										</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceExtendAmortization"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceIncreaseLoanSize">
											Refinance and increase the size of the loan
										</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceIncreaseLoanSize"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceNewLender">
											I am looking to exit the relationship with the current lender
										</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceNewLender"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									<Box sx={{ marginBottom: 3 }}>
										<CerebroLabel htmlFor="refinanceOther">Other</CerebroLabel>
										<Field
											small
											as={CerebroYesNo}
											name="refinanceOther"
											disabled={isSubmitting}
											error={error}
										/>
									</Box>

									{errors.refinanceMaturingSoon && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.refinanceMaturingSoon}
										</CerebroText>
									)}

									<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
										<Grid item>
											<CerebroPrimaryButton loading={isSubmitting} type="submit">
												Save Details
											</CerebroPrimaryButton>
										</Grid>
									</Grid>
								</CerebroForm>
							);
						}}
					</Formik>
				</Box>
			</CerebroDrawer>
		</>
	);
};

interface DealRequestAllocationRefinanceValues {
	refinanceMaturingSoon?: boolean;
	refinanceLowerRate?: boolean;
	refinanceExtendAmortization?: boolean;
	refinanceIncreaseLoanSize?: boolean;
	refinanceNewLender?: boolean;
	refinanceOther?: boolean;
}

interface DealRequestAllocationRefinanceValueErrors {
	refinanceMaturingSoon?: string;
	refinanceLowerRate?: string;
	refinanceExtendAmortization?: string;
	refinanceIncreaseLoanSize?: string;
	refinanceNewLender?: string;
	refinanceOther?: string;
}

function validateForm(values: DealRequestAllocationRefinanceValues) {
	const errors: DealRequestAllocationRefinanceValueErrors = {};

	if (
		!values.refinanceMaturingSoon &&
		!values.refinanceLowerRate &&
		!values.refinanceExtendAmortization &&
		!values.refinanceIncreaseLoanSize &&
		!values.refinanceNewLender &&
		!values.refinanceOther
	) {
		errors.refinanceMaturingSoon = 'You must select yes to at least one refinancing reason.';
	}

	return errors;
}

export function isRefinanceEmpty(allocation: DealRequestAllocation): boolean {
	return (
		!allocation.refinanceMaturingSoon &&
		!allocation.refinanceLowerRate &&
		!allocation.refinanceExtendAmortization &&
		!allocation.refinanceIncreaseLoanSize &&
		!allocation.refinanceNewLender &&
		!allocation.refinanceOther
	);
}
